import { useState, useRef, useCallback } from 'react';
import { listUnifiedJobs } from '../../../../services/URO';

const PAGE_SIZE = 20;

/**
 * Simple pagination hook for gallery jobs
 */
export function useGalleryPagination(filters) {
  const [jobs, setJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const pageRef = useRef(0);
  const isFetchingRef = useRef(false);

  const normalizeJob = (job) => ({
    ...job,
    id: job.job_id || '',
    type: job.job_type || '',
  });

  const buildParams = useCallback((page) => {
    const params = { page, limit: PAGE_SIZE };
    if (filters.job_type) params.job_type = filters.job_type;
    if (filters.status) params.status = filters.status;
    if (filters.model_id) params.model_id = filters.model_id;
    if (filters.workflow_id) params.workflow_definition_id = filters.workflow_id;
    return params;
  }, [filters]);

  const fetchInitial = useCallback(async () => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    setIsLoading(true);
    pageRef.current = 1;

    try {
      const result = await listUnifiedJobs(buildParams(1));
      const items = (result?.data?.items || []).map(normalizeJob);
      setJobs(items);
      setHasMore(result?.data?.pagination?.hasNextPage ?? items.length === PAGE_SIZE);
    } catch (err) {
      console.error('Failed to fetch jobs:', err);
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, [buildParams]);

  const fetchMore = useCallback(async () => {
    if (isFetchingRef.current || !hasMore) return;
    isFetchingRef.current = true;
    setIsLoadingMore(true);

    const nextPage = pageRef.current + 1;

    try {
      const result = await listUnifiedJobs(buildParams(nextPage));
      const items = (result?.data?.items || []).map(normalizeJob);
      setJobs((prev) => [...prev, ...items]);
      setHasMore(result?.data?.pagination?.hasNextPage ?? items.length === PAGE_SIZE);
      pageRef.current = nextPage;
    } catch (err) {
      console.error('Failed to fetch more jobs:', err);
    } finally {
      setIsLoadingMore(false);
      isFetchingRef.current = false;
    }
  }, [buildParams, hasMore]);

  const reset = useCallback(() => {
    setJobs([]);
    setIsLoading(true);
    setIsLoadingMore(false);
    setHasMore(true);
    pageRef.current = 0;
    isFetchingRef.current = false;
  }, []);

  return {
    jobs,
    setJobs,
    isLoading,
    isLoadingMore,
    hasMore,
    fetchInitial,
    fetchMore,
    reset,
  };
}

export default useGalleryPagination;
